import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@arcadeai/design-system/components/ui/atoms/dialog";
import { Arcade as ArcadeLogo } from "@arcadeai/design-system/components/ui/atoms/icons/arcade";
import { MobileTooltipProvider } from "@arcadeai/design-system/components/ui/atoms/mobile-tooltip";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import { Package, Wrench, X } from "lucide-react";
import { useState } from "react";
import { ActionButtons } from "./components/action-buttons";
import { FilterBadges } from "./components/filter-badges";
import { FooterSummary } from "./components/footer-summary";
import { SearchInput } from "./components/search-input";
import { SelectButton } from "./components/select-button";
import { ToolkitList } from "./components/toolkit-list";
import { ToolsList } from "./components/tools-list";
import { useToolkitPicker } from "./hooks/use-toolkit-picker";
import type { ToolkitPickerProps } from "./index";

const MAC_REGEX = /Mac/i;

export const ToolkitPickerDialog = (props: ToolkitPickerProps) => {
  const {
    description = "Select MCP servers and tools",
    children,
    selectionMode = "multi",
    onSelectionChange,
    defaultToolFqns,
  } = props;

  const [open, setOpen] = useState(false);
  const [toolkitsScrollEl, setToolkitsScrollEl] =
    useState<HTMLDivElement | null>(null);
  const [toolsScrollEl, setToolsScrollEl] = useState<HTMLDivElement | null>(
    null
  );

  const {
    state: {
      selectedToolkits,
      selectedTools,
      activeTab,
      filteredToolkits,
      filteredTools,
    },
    setActiveTab,
    clearSearchQuery,
    commitSelections,
    restoreCommittedState,
  } = useToolkitPicker();

  const handleSubmit = () => {
    commitSelections();
    if (onSelectionChange) {
      onSelectionChange({
        selectedToolkits: Array.from(selectedToolkits.keys()),
        selectedTools: Array.from(selectedTools.keys()),
      });
    }
    setOpen(false);
  };

  return (
    <MobileTooltipProvider>
      <Dialog
        onOpenChange={(newOpen) => {
          setOpen(newOpen);
          if (newOpen) {
            // Restore committed selections when dialog opens
            restoreCommittedState();
            clearSearchQuery();
            setActiveTab("toolkits");
          }
        }}
        open={open}
      >
        <DialogTrigger render={children} />
        <DialogContent
          className="flex max-h-[80vh] min-h-80 w-[95vw] max-w-xl flex-col gap-2 p-0 sm:max-w-2xl md:max-w-3xl lg:max-w-4xl xl:max-w-5xl 2xl:max-w-7xl"
          data-testid="toolkit-picker-dialog"
          showCloseButton={false}
        >
          <DialogHeader className="shrink-0 p-4 pb-0 sm:p-6 sm:pb-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative flex size-10 items-center justify-center overflow-hidden rounded-xl shadow-lg ring-1 ring-black/5 sm:size-12">
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 z-0 rounded-xl bg-[radial-gradient(at_15%_20%,rgba(249,115,22,0.9),transparent_45%),radial-gradient(at_10%_85%,rgba(14,165,233,0.9),transparent_45%),radial-gradient(at_95%_25%,rgba(236,72,153,0.9),transparent_40%),linear-gradient(135deg,rgba(14,165,233,0.6)_0%,rgba(236,72,153,0.6)_50%,rgba(249,115,22,0.6)_100%)]"
                  />
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 z-10 bg-[repeating-conic-gradient(from_0deg_at_100%_0%,rgba(0,0,0,0.25)_0deg_1deg,transparent_1deg_12deg)] opacity-30"
                  />
                  <span className="relative z-20">
                    <ArcadeLogo className="size-5 sm:size-7" />
                  </span>
                </div>
                <div className="ml-1 text-left">
                  <DialogTitle className="font-bold text-lg sm:text-xl">
                    Choose Your Tools
                  </DialogTitle>
                  <DialogDescription className="hidden sm:block">
                    {description}
                  </DialogDescription>
                </div>
              </div>
              <DialogClose
                render={
                  <Button
                    className="h-8 w-8 rounded-full p-0"
                    data-testid="toolkit-picker-close"
                    size="sm"
                    variant="ghost"
                  />
                }
              >
                <X className="h-4 w-4" />
              </DialogClose>
            </div>

            <div className="mb-2 space-y-2 pt-2 sm:space-y-4 sm:pt-4">
              <SearchInput />
            </div>
          </DialogHeader>

          <Tabs
            className="flex min-h-0 flex-1 flex-col gap-0.5"
            // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
            onValueChange={(v) => setActiveTab(v as "toolkits" | "tools")}
            value={activeTab}
          >
            <div className="shrink-0 space-y-2 px-4 sm:space-y-3 sm:px-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <TabsList className="h-10 w-auto gap-1 rounded-md border border-neutral-200 bg-white/60 p-1 shadow-sm dark:border-neutral-800 dark:bg-neutral-900/60">
                  <TabsTrigger
                    className="h-8 rounded-md px-3 font-medium text-neutral-600 text-sm hover:bg-black/5 data-active:bg-white data-active:text-neutral-900 dark:text-neutral-300 dark:data-active:bg-neutral-800 dark:data-active:text-white dark:hover:bg-white/10"
                    data-testid="toolkits-tab"
                    value="toolkits"
                  >
                    <Package className="h-4 w-4" />
                    <span>MCP Servers</span>
                    <span className="ml-1 rounded-full bg-emerald-300 px-1.5 py-0.5 text-emerald-900 text-xs dark:bg-emerald-500/25 dark:text-emerald-200">
                      {filteredToolkits.size}
                    </span>
                  </TabsTrigger>
                  <TabsTrigger
                    className="h-8 rounded-md px-3 font-medium text-neutral-600 text-sm hover:bg-black/5 data-active:bg-white data-active:text-neutral-900 dark:text-neutral-300 dark:data-active:bg-neutral-800 dark:data-active:text-white dark:hover:bg-white/10"
                    data-testid="tools-tab"
                    value="tools"
                  >
                    <Wrench className="h-4 w-4" />
                    <span>Tools</span>
                    <span className="ml-1 rounded-full bg-emerald-300 px-1.5 py-0.5 text-emerald-900 text-xs dark:bg-emerald-500/25 dark:text-emerald-200">
                      {filteredTools.size}
                    </span>
                  </TabsTrigger>
                </TabsList>

                <ActionButtons
                  defaultToolFqns={defaultToolFqns}
                  selectionMode={selectionMode}
                />
              </div>

              <FilterBadges />
            </div>

            <TabsContent
              className="min-h-0 flex-1 overflow-y-auto px-4 py-2 sm:px-6"
              data-testid="content-area"
              ref={setToolkitsScrollEl}
              value="toolkits"
            >
              <ToolkitList
                scrollContainer={toolkitsScrollEl}
                selectionMode={selectionMode}
              />
            </TabsContent>

            <TabsContent
              className="min-h-0 flex-1 overflow-y-auto px-4 py-2 sm:px-6"
              data-testid="content-area"
              ref={setToolsScrollEl}
              value="tools"
            >
              <ToolsList
                scrollContainer={toolsScrollEl}
                selectionMode={selectionMode}
              />
            </TabsContent>
          </Tabs>

          <DialogFooter className="mx-0 mb-0 shrink-0 rounded-b-xl border-t bg-white px-4 py-3 sm:px-6 sm:py-4 dark:bg-neutral-950">
            <div
              className={`${selectionMode === "single" ? "sm:justify-end" : "sm:justify-between"} flex w-full flex-col gap-3 sm:flex-row sm:items-center`}
            >
              {selectionMode === "multi" && <FooterSummary />}
              <div className="mt-2 flex gap-2 sm:mt-0 sm:gap-2">
                <DialogClose
                  render={
                    <Button
                      className="flex-1 sm:flex-none"
                      data-testid="cancel-button"
                      size="sm"
                      variant="outline"
                    />
                  }
                >
                  Cancel
                </DialogClose>
                <TooltipProvider delay={350}>
                  <Tooltip>
                    <TooltipTrigger render={<span />}>
                      <SelectButton
                        className="flex-1 sm:flex-none"
                        onCommit={handleSubmit}
                        selectedCount={selectedTools.size}
                        selectionMode={selectionMode}
                      />
                    </TooltipTrigger>
                    <TooltipContent>
                      <div className="flex items-center gap-2">
                        <span>Submit selection</span>
                        <div className="flex items-center gap-1">
                          <kbd className="inline-flex h-4 items-center rounded border bg-muted px-1 font-mono text-[10px]">
                            {typeof window !== "undefined" &&
                            MAC_REGEX.test(navigator.userAgent)
                              ? "⌘"
                              : "Ctrl"}
                          </kbd>
                          <span className="text-xs">+</span>
                          <kbd className="inline-flex h-4 items-center rounded border bg-muted px-1 font-mono text-[10px]">
                            ↵
                          </kbd>
                        </div>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MobileTooltipProvider>
  );
};
